import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API_CONFIG from '../config/api';
import { shouldRedirectTeacherDashboardToPolicy } from '../utils/stagingWebApp';
import { captureFeedbackIntentFromSearchParams } from '../utils/feedbackDeepLink';
import { ensureFreshAccessToken, silentRefreshAccessToken } from '../utils/silentAuthRefresh';

const AppRouterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    const fetchProfileWithRetry = async (token: string) => {
      const primaryUrl = `${API_CONFIG.API_BASE_URL}/auth/me`;
      const fallbackUrl = `${API_CONFIG.API_BASE_URL_FULL.replace('/api', '')}/auth/me`;
      const urls = [primaryUrl, fallbackUrl];
      let lastError: Error | null = null;
      let activeToken = token;

      for (const url of urls) {
        for (let attempt = 1; attempt <= 2; attempt++) {
          try {
            const response = await fetch(url, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${activeToken}`,
                'Content-Type': 'application/json'
              }
            });

            if (response.ok) {
              return response.json();
            }

            // Expired JWT: try silent refresh once, then retry /auth/me.
            if (response.status === 401) {
              const refreshed = await silentRefreshAccessToken({
                force: true,
                token: activeToken
              });
              if (refreshed.ok && refreshed.token) {
                activeToken = refreshed.token;
                const retryResponse = await fetch(url, {
                  method: 'GET',
                  headers: {
                    'Authorization': `Bearer ${activeToken}`,
                    'Content-Type': 'application/json'
                  }
                });
                if (retryResponse.ok) {
                  return retryResponse.json();
                }
              }
              throw new Error(`Unauthorized (${response.status})`);
            }

            if (response.status === 403) {
              throw new Error(`Unauthorized (${response.status})`);
            }

            if (attempt < 2) {
              await sleep(300);
            } else {
              throw new Error(`Profile fetch failed at ${url}: ${response.status}`);
            }
          } catch (error) {
            lastError = error as Error;
            if (String((error as Error)?.message || '').startsWith('Unauthorized')) {
              throw error;
            }
            if (attempt < 2) {
              await sleep(300);
            }
          }
        }
      }

      throw lastError || new Error('Failed to fetch user profile');
    };

    const routeUser = async () => {
      try {
        captureFeedbackIntentFromSearchParams(searchParams);

        // Check for token in URL params (from OAuth callback with token in URL)
        const token = searchParams.get('token');
        
        if (token) {
          // Save token for API calls
          localStorage.setItem('trafficLiteToken', token);
        }

        // Fetch user profile from /me endpoint
        // Token should be in localStorage from backend callback (stored via script tag)
        let savedToken = localStorage.getItem('trafficLiteToken');
        
        if (!savedToken) {
          console.error('No token found');
          navigate('/signin');
          return;
        }

        // Slide JWT if expired/expiring before routing into Dashboard.
        const ensured = await ensureFreshAccessToken({ token: savedToken });
        if (ensured) {
          savedToken = ensured;
        }

        const userData = await fetchProfileWithRetry(savedToken);
        console.log('✅ User profile fetched:', userData);

        const tokenForSync = localStorage.getItem('trafficLiteToken') || savedToken;

        // Save user data to localStorage so App.tsx can read it
        localStorage.setItem('trafficLiteUser', JSON.stringify({
          email: userData.email,
          role: userData.role,
          name: userData.name,
          isAdmin: userData.isAdmin,
          isSuperAdmin: userData.isSuperAdmin
        }));

        // Phase 3: Sync token and profile to extension via postMessage
        // The content script bridge will forward this to the extension background
        const profileData = {
          email: userData.email,
          role: userData.role,
          name: userData.name,
          isAdmin: userData.isAdmin,
          isSuperAdmin: userData.isSuperAdmin,
          id: userData.id
        };
        
        console.log('🔄 Sending SYNC_AUTH_TOKEN postMessage to extension...');
        window.postMessage(
          {
            source: 'trafficlite-web',
            action: 'SYNC_AUTH_TOKEN',
            token: tokenForSync,
            profile: profileData
          },
          window.location.origin
        );
        console.log('✅ SYNC_AUTH_TOKEN postMessage sent to extension');

        // Route based on role and privilege.
        if (userData.isSuperAdmin) {
          navigate('/admin/users');
        } else if (userData.isAdmin) {
          navigate('/admin/settings');
        } else if (userData.role === 'teacher') {
          navigate(shouldRedirectTeacherDashboardToPolicy() ? '/teacher/policy-settings' : '/dashboard');
        } else {
          navigate(shouldRedirectTeacherDashboardToPolicy() ? '/teacher/policy-settings' : '/dashboard');
        }
      } catch (error) {
        console.error('❌ Error routing user:', error);
        // On error, redirect to signin
        navigate('/signin?error=authentication_failed');
      } finally {
        setLoading(false);
      }
    };

    routeUser();
  }, [navigate, searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your account...</p>
        </div>
      </div>
    );
  }

  return null;
};

export default AppRouterPage;
